import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaPlay } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const ShowcaseSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const showcaseItems = [
    {
      type: 'image',
      src: '/sheba-total-control.png',
      title: t('showcase.item1Title'),
      description: t('showcase.item1Desc'),
    },
    {
      type: 'image',
      src: '/sheba-security.png',
      title: t('showcase.item2Title'),
      description: t('showcase.item2Desc'),
    },
    {
      type: 'video',
      src: 'https://customer-assets.emergentagent.com/job_smart-future-4/artifacts/a4x1kct4_WhatsApp%20Video%202026-02-27%20at%2020.35.50.mp4',
      poster: '/sheba-total-control.png',
      title: t('showcase.item3Title'),
      description: t('showcase.item3Desc'),
    },
    {
      type: 'video',
      src: 'https://customer-assets.emergentagent.com/job_smart-future-4/artifacts/o7vgtgxp_WhatsApp%20Video%202026-02-27%20at%2020.35.51.mp4',
      poster: '/sheba-security.png',
      title: t('showcase.item4Title'),
      description: t('showcase.item4Desc'),
    },
  ];

  return (
    <section
      id="showcase"
      ref={ref}
      className="section-padding bg-gradient-to-b from-[#0a0e27] to-[#0f1640] relative overflow-hidden"
      data-testid="showcase-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <div className="inline-block glass px-6 py-2 rounded-full mb-6">
            <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
              {t('showcase.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('showcase.title')}
            <span className="gradient-text block mt-2">{t('showcase.subtitle')}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            {t('showcase.description')}
          </p>
        </motion.div>

        {/* Showcase Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {showcaseItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15 }}
              whileHover={{ y: -10 }}
              className="group"
            >
              <div className="glass-strong rounded-3xl overflow-hidden h-full">
                {/* Media Container */}
                <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#0a0e27] to-[#1a1e47]">
                  {item.type === 'image' ? (
                    <motion.img
                      src={item.src}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      whileHover={{ scale: 1.05 }}
                    />
                  ) : (
                    <div className="relative w-full h-full">
                      <video
                        className="w-full h-full object-cover"
                        poster={item.poster}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls
                      >
                        <source src={item.src} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                  
                  {/* Gradient Overlay for images */}
                  {item.type === 'image' && (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27] via-transparent to-transparent" />
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#00d4ff] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
        >
          {[
            { icon: '🏠', label: t('showcase.feature1') },
            { icon: '🏢', label: t('showcase.feature2') },
            { icon: '🔐', label: t('showcase.feature3') },
            { icon: '📱', label: t('showcase.feature4') },
          ].map((feature, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="glass rounded-2xl p-6 text-center"
            >
              <div className="text-4xl mb-3">{feature.icon}</div>
              <p className="text-white font-semibold">{feature.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ShowcaseSection;
